import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Button, Row, Col, Label, ButtonGroup } from 'reactstrap';
import { AvForm, AvGroup, AvInput, AvField } from 'availity-reactstrap-validation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IRootState } from 'app/shared/reducers';

import { getEntity, updateEntity, createEntity, reset } from './rejection-reason.reducer';
import { hasAnyPermission } from 'app/shared/auth/permissions';
import { getAllByType as getCountries } from 'app/entities/region/region.reducer';
import { IRegion } from 'app/shared/model/region.model';

export interface IRejectionReasonAmbulanceUpdateProps extends StateProps, DispatchProps, RouteComponentProps<{ id: string }> {}

export const RejectionReasonAmbulanceUpdate = (props: IRejectionReasonAmbulanceUpdateProps) => {
  const { rejectionReasonAmbulanceEntity, countries, loading, updating, isAdmin, userCountryId } = props;
  const [isNew, setIsNew] = useState(!props.match.params || !props.match.params.id);
  const [activeCountry, setActiveCountry] = useState<IRegion>({});
  const [activeStatus, setActiveStatus] = useState(true);

  const handleClose = () => {
    props.history.push('/rejection-reasons-ambulance');
  };

  useEffect(() => {
    if (!countries || countries.length === 0) {
      props.getCountries();
    }
  }, []);

  useEffect(() => {
    if (countries && countries.length > 0) {
      const ac = countries.find(country => country.id === userCountryId);
      setActiveCountry(ac);
    }
  }, [countries, userCountryId]);

  useEffect(() => {
    if (!isNew) {
      setActiveStatus(rejectionReasonAmbulanceEntity.isActive);
    }
  }, [rejectionReasonAmbulanceEntity]);

  useEffect(() => {
    if (isNew) {
      props.reset();
    } else {
      props.getEntity(props.match.params.id);
    }
  }, []);

  useEffect(() => {
    if (props.updateSuccess) {
      handleClose();
    }
  }, [props.updateSuccess]);

  const saveEntity = (event, errors, values) => {
    if (errors.length === 0) {
      const cid = isNew ? activeCountry?.id : rejectionReasonAmbulanceEntity.countryId;
      const country = countries.find(c => c.id === cid);

      const entity = {
        ...rejectionReasonAmbulanceEntity,
        ...values,
        isActive: activeStatus,
        country
      };

      if (isNew) {
        props.createEntity(entity);
      } else {
        props.updateEntity(entity);
      }
    }
  };

  const regCountriesList = countries.filter((ele) => {
    return isAdmin ? 1 : ele.id === activeCountry?.id;
  });

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="mmamaApp.rejectionReasonAmbulance.home.createOrEditLabel">Create or edit a Rejection Reason (Ambulance)</h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <AvForm model={isNew ? {} : rejectionReasonAmbulanceEntity} onSubmit={saveEntity}>
              {!isNew ? (
                <AvGroup>
                  <Label for="rejection-reason-ambulance-id">ID</Label>
                  <AvInput id="rejection-reason-ambulance-id" type="text" className="form-control" name="id" required readOnly />
                </AvGroup>
              ) : null}

              <AvGroup>
                <Label id="isActive">
                  Active Status
                </Label>
                <ButtonGroup style={{ marginLeft: '30px' }}>
                  <Button
                    color={activeStatus ? 'primary' : 'secondary'}
                    onClick={() => {
                      setActiveStatus(true);
                    }}
                  >
                    Active
                  </Button>

                  <Button
                    color={!activeStatus ? 'primary' : 'secondary'}
                    onClick={() => {
                      setActiveStatus(false);
                    }}
                  >
                    Inactive
                  </Button>
                </ButtonGroup>
              </AvGroup>

              <AvGroup>
                <Label id="enReasonLabel" for="rejection-reason-en">
                  Rejection Reason (English)
                </Label>
                <AvField
                  id="rejection-reason-en"
                  type="text"
                  name="enRejectionReason"
                  validate={{
                    required: { value: true, errorMessage: 'This field is required.' },
                  }}
                />
              </AvGroup>
              <AvGroup>
                <Label id="stReasonLabel" for="rejection-reason-st">
                  Rejection Reason (Local Language)
                </Label>
                <AvField
                  id="rejection-reason-st"
                  type="text"
                  name="stRejectionReason"
                  validate={{
                    required: { value: true, errorMessage: 'This field is required.' },
                  }}
                />
              </AvGroup>

              {regCountriesList && regCountriesList.length > 0 && (
                <AvGroup>
                  <Label for="rejection-reason-country">Country</Label>
                  <AvInput id="rejection-reason-country" type="select" className="form-control" name="countryId" value={isNew ? activeCountry?.id : rejectionReasonAmbulanceEntity.countryId}>
                    {regCountriesList
                      ? regCountriesList.map(country => (
                        <option value={country.id} key={country.id}>
                          {country.name}
                        </option>
                      ))
                      : null}
                  </AvInput>
                </AvGroup>
              )}

              <Button tag={Link} id="cancel-save" to="/rejection-reasons-ambulance" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">Back</span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp; Save
              </Button>
            </AvForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  rejectionReasonAmbulanceEntity: storeState.rejectionReasonAmbulance.entity,
  loading: storeState.rejectionReasonAmbulance.loading,
  updating: storeState.rejectionReasonAmbulance.updating,
  updateSuccess: storeState.rejectionReasonAmbulance.updateSuccess,
  countries: storeState.region.countries,
  userCountryId: storeState.authentication.userCountryId,
  isAdmin: hasAnyPermission(storeState.authentication.account.roles, ['ADMIN'])
});

const mapDispatchToProps = {
  getEntity,
  updateEntity,
  createEntity,
  reset,
  getCountries,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(RejectionReasonAmbulanceUpdate);
